// « Mon compte » : self-service profile, avatar, password and activity, plus read-only access to
// another user's profile/activity. All calls go through the shared `axios` instance so the
// Auth0AxiosBridge interceptor attaches the admin Bearer token on deployed instances.
//
// HAND-WRITTEN client : lives beside its consumer because `src/API/` is regenerated wholesale.
import axios from "axios";
import type { LogEntry } from "../../API/Models";

const apiBase = (process.env.REACT_APP_API_URL ?? "").replace(/\/+$/, "");

export interface AccountProfile {
	id?: string | null;
	email?: string | null;
	displayName?: string | null;
	avatarKey?: string | null;
	pictureFallback?: string | null;
	roles: string[];
	canChangePassword: boolean;
}

export interface UserProfile {
	id: string;
	email?: string | null;
	displayName?: string | null;
	avatarKey?: string | null;
	pictureFallback?: string | null;
	roles: string[];
}

export interface ActivityPage {
	items: LogEntry[];
	total: number;
	page: number;
	pageSize: number;
}

export interface ChangePasswordResult {
	status: string; // changed | email-sent | wrong-password | policy | not-supported
	message: string;
}

export async function getMyProfile(): Promise<AccountProfile> {
	const r = await axios.get<AccountProfile>(`${apiBase}/api/account/profile`, { withCredentials: true });
	return r.data;
}

export async function uploadMyAvatar(file: File): Promise<{ avatarKey: string }> {
	const form = new FormData();
	form.append("File", file);
	const r = await axios.post<{ avatarKey: string }>(`${apiBase}/api/account/avatar`, form, {
		withCredentials: true,
		headers: { "Content-Type": "multipart/form-data" }
	});
	return r.data;
}

/** Axes de filtre du journal d'un utilisateur (miroir des paramètres serveur ; tous optionnels). */
export interface ActivityFilter {
	type?: string;
	level?: string;
	/** ISO UTC : entrées à partir de cet instant. */
	since?: string;
	/** ISO UTC : entrées strictement avant cet instant. */
	until?: string;
	/** Texte cherché dans le message ou les détails. */
	q?: string;
}

const activityParams = (page: number, pageSize: number, filter?: ActivityFilter) => ({
	page,
	pageSize,
	type: filter?.type || undefined,
	level: filter?.level || undefined,
	since: filter?.since || undefined,
	until: filter?.until || undefined,
	q: filter?.q?.trim() || undefined
});

export async function getMyActivity(page = 1, pageSize = 30, filter?: ActivityFilter): Promise<ActivityPage> {
	const r = await axios.get<ActivityPage>(`${apiBase}/api/account/activity`, {
		params: activityParams(page, pageSize, filter),
		withCredentials: true
	});
	return r.data ?? { items: [], total: 0, page, pageSize };
}

export async function changeMyPassword(currentPassword: string, newPassword: string): Promise<ChangePasswordResult> {
	const r = await axios.post<ChangePasswordResult>(
		`${apiBase}/api/account/change-password`,
		{ currentPassword, newPassword },
		{ withCredentials: true }
	);
	return r.data;
}

export async function getUserProfile(userId: string): Promise<UserProfile> {
	const r = await axios.get<UserProfile>(
		`${apiBase}/api/admin-users/${encodeURIComponent(userId)}/profile`,
		{ withCredentials: true }
	);
	return r.data;
}

export async function getUserActivity(userId: string, page = 1, pageSize = 30, filter?: ActivityFilter): Promise<ActivityPage> {
	const r = await axios.get<ActivityPage>(
		`${apiBase}/api/admin-users/${encodeURIComponent(userId)}/activity`,
		{ params: activityParams(page, pageSize, filter), withCredentials: true }
	);
	return r.data ?? { items: [], total: 0, page, pageSize };
}

export interface DirectoryEntry {
	id: string;
	name?: string | null;
	email?: string | null;
	/** Auth0/Google profile photo when available (used for author avatars). */
	picture?: string | null;
	/** Storage key of the avatar uploaded in the admin : wins over `picture`. */
	avatarKey?: string | null;
	/** Badges des rôles MAXIMAUX (dominance calculée serveur : admin mange tout, internal jamais
	 *  listé). Portés par la barre de présence et la carte Équipe du dashboard. */
	badges?: Array<{ roleKey: string; label: string; color: string; kind: string }>;
}

/** Slim id/name/email directory used to turn an actor NAME (audit logs carry only a name) into a
 *  profile link. Requires feature:profile:read; callers should guard on that and treat a failure as
 *  "no links" rather than an error. */
export async function getUserDirectory(): Promise<DirectoryEntry[]> {
	const r = await axios.get<DirectoryEntry[]>(`${apiBase}/api/admin-users/directory`, { withCredentials: true });
	return r.data ?? [];
}
